import { useEffect, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useNotification } from 'context/notification';
import fetchExpresso from 'utility/fetchExpresso';
import EventForm from 'components/forms/EventForm';
import moment from 'moment-timezone';
import { Page } from 'components/ui/layout';
import useSeasonEventSave from 'hooks/useSeasonEventSave';

// Default the timezone field to the current users timezone
const baseDefaultValues = {
    timezone: moment.tz.guess(),
};

export default function EventCreator(props) {
    const { hostId, onCreate, onCancel, parentMode = 0, parentEventId = null } = props;

    const [initStatus, setInitStatus] = useState('loading'); // loading, error, ready
    const { save, loading, confirmationDialog } = useSeasonEventSave();
    const [seasons, setSeasons] = useState([]);

    const [hostSeatsEnabled, setHostSeatsEnabled] = useState(false);
    const [seatingCharts, setSeatingCharts] = useState([]);
    const [defaultValues, setDefaultValues] = useState({
        ...baseDefaultValues,
        parentMode,
        parentEventId,
    });

    const { notify } = useNotification();

    useEffect(() => {
        (async () => {
            try {
                const [chartsResponse, hostResponse, eventsResponse] = await Promise.all([
                    fetchExpresso(`/apiv2/hosts/${hostId}/seating_charts/`),
                    fetchExpresso(`/apiv2/hosts/${hostId}`),
                    fetchExpresso(`/apiv2/events/?host=${hostId}`),
                ]);

                let seasonDefaults = {};
                if (eventsResponse.ok) {
                    const events = await eventsResponse.json();
                    const selectedSeason = events.find(
                        (event) => String(event.event_id) === String(parentEventId),
                    );

                    seasonDefaults = selectedSeason
                        ? {
                              type: selectedSeason.event_type_id,
                              venueName: selectedSeason.address1,
                              venueAddress: selectedSeason.address2,
                              city: selectedSeason.city,
                              province: selectedSeason.prov_state,
                              postalCode: selectedSeason.postal_code,
                              country: selectedSeason.country,
                              timezone: selectedSeason.time_zone,
                              taxName: selectedSeason.tax_name,
                              taxRate: selectedSeason.tax_rate
                                  ? parseFloat((selectedSeason.tax_rate * 100).toFixed(4))
                                  : null,
                              posTaxRate: selectedSeason.pos_tax_rate
                                  ? parseFloat((selectedSeason.pos_tax_rate * 100).toFixed(4))
                                  : null,
                          }
                        : {};

                    setSeasons(
                        events.filter(
                            (event) =>
                                Number(event.parent_mode) === 1 &&
                                !moment(event.end_date)
                                    .tz(event.time_zone, true)
                                    .isBefore(moment()),
                        ),
                    );
                }

                if (chartsResponse.status === 200) {
                    const data = await chartsResponse.json();
                    const charts = data.charts;

                    setSeatingCharts(charts);
                    setHostSeatsEnabled(true);
                }

                if (hostResponse.status === 200) {
                    const host = await hostResponse.json();
                    let country = undefined;

                    if (host.country === 'CA') country = 'Canada';
                    if (host.country === 'US') country = 'United States';

                    setDefaultValues({
                        ...baseDefaultValues,
                        parentMode,
                        parentEventId,
                        ...seasonDefaults,
                        country: seasonDefaults.country || country,
                    });
                }

                setInitStatus('ready');
            } catch (error) {
                // we allow the host to continue even if they won't be able to select a chart
                // they can always set it up later
                setInitStatus('ready');
            }
        })();
    }, [hostId, parentMode, parentEventId]);

    const handleSubmit = (formData) => {
        const body = {
            ...formData,
            hostId,
            taxRate: formData.taxRate ? formData.taxRate / 100 : null,
            posTaxRate: formData.posTaxRate ? formData.posTaxRate / 100 : null,
        };
        if (body.ctaButtonURL) body.ctaButtonURL = 'https://' + body.ctaButtonURL;
        save({
            endpoint: '/apiv2/events/',
            method: 'POST',
            body,
            fallback: `There was a problem creating your ${parentMode ? 'season' : 'event'}`,
            onSuccess: (data) => {
                notify.success(parentMode ? 'Season Created' : 'Event Created');
                onCreate(data.eventId);
            },
        });
    };

    if (initStatus === 'loading') {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" width={1} height={1}>
                <CircularProgress />
            </Box>
        );
    }

    if (initStatus === 'error') {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" width={1} height={1}>
                <Typography align="center">There was a problem loading your form</Typography>
            </Box>
        );
    }

    return (
        <Page width="md">
            {confirmationDialog}
            <EventForm
                defaultValues={defaultValues}
                onSubmit={handleSubmit}
                onCancel={onCancel}
                loading={loading}
                hostSeatsEnabled={hostSeatsEnabled}
                seatingCharts={seatingCharts}
                seasons={seasons}
            />
        </Page>
    );
}
